import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminApi } from '../api/admin.api';

interface User {
  _id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
}

export const useAuth = () => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
  });

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('adminJwt');
    
    if (!token) {
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
      });
      return;
    }

    try {
      const response = await adminApi.profile();
      setAuthState({
        isAuthenticated: true,
        user: response.data as unknown as User,
        isLoading: false,
      });
    } catch (error) {
      localStorage.removeItem('adminJwt');
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
      });
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    try {
      setAuthState((s) => ({ ...s, isLoading: true }));
      const response = await adminApi.login(username, password);

      const { accessToken, refreshToken, user } = response.data;
      
      localStorage.setItem('adminJwt', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      
      setAuthState({
        isAuthenticated: true,
        user: user as unknown as User,
        isLoading: false,
      });
      
      toast.success('Login successful');
      return { success: true };
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed';
      setAuthState((s) => ({ ...s, isLoading: false }));
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await adminApi.logout();
    } catch {
      // ignore
    } finally {
      localStorage.removeItem('adminJwt');
      localStorage.removeItem('refreshToken');
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
      });
      navigate('/login');
      toast.success('Logged out successfully');
    }
  }, [navigate]);

  const refreshToken = useCallback(async () => {
    const token = localStorage.getItem('refreshToken');
    
    if (!token) {
      logout();
      return;
    }

    try {
      const response = await adminApi.refresh(token);
      localStorage.setItem('adminJwt', response.data.accessToken);
      await checkAuth();
    } catch (error) {
      logout();
    }
  }, [checkAuth, logout]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return {
    ...authState,
    login,
    logout,
    refreshToken,
    checkAuth,
  };
};